import { Button } from '@/shared/ui/button'
import { Typography } from '@/shared/ui/typography'

import { type FC } from 'react'

import { SideMenuLink } from './SideMenuLink'
import type { SideMenuActionProps } from './types'
import { useCloseOnMobile } from './useCloseOnMobile'

/**
 * SideMenu.Action — a non-navigational action.
 *
 * - `intent="primary"` renders the DS Button (outline) as a promoted CTA.
 *   Use it in the body for a single emphasised action (e.g. "Workflow").
 * - `intent="secondary"` (default) renders the rail primitive shared with
 *   Item/SubItem — same icon column, same hover/focus tint — so footer
 *   actions align visually with the navigation entries.
 *
 * See SideMenuItem for why the rail primitive does not reuse `<Button>`.
 */
export const SideMenuAction: FC<SideMenuActionProps> = ({
  icon,
  label,
  intent = 'secondary',
  to,
  onClick,
}) => {
  const closeOnMobile = useCloseOnMobile()

  if (intent === 'primary') {
    const handleClick = () => {
      closeOnMobile()
      onClick?.()
    }
    return (
      <Button
        variant="outline"
        intent="neutral"
        size="md"
        to={to}
        onClick={to ? closeOnMobile : handleClick}
        className="side-menu-cta"
      >
        {icon}
        {label}
      </Button>
    )
  }

  return (
    <SideMenuLink to={to} onClick={onClick} baseClassName="side-menu-item">
      <span className="side-menu-item-content">
        <span className="side-menu-item-icon">{icon}</span>
        <span className="side-menu-item-label">
          <Typography type="paragraph" variant="md">
            {label}
          </Typography>
        </span>
      </span>
    </SideMenuLink>
  )
}

SideMenuAction.displayName = 'SideMenuAction'
